import React, { useState, useRef, useMemo } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import { Loader } from '../../../components';
import { arrowLeftIcon, arrowRightIcon } from '../../assets/index';
import classNames from 'classnames';
import chunk from 'lodash/chunk';
import CoursesTitle from '../courses-title';
import Button from '../../../components/button/index';
import CourseCard from '../course-card/index';
import SingleCourseCard from '../single-course-card';
import CoursesCollapsibleSheet from '../courses-collapsible-sheet';
import ShowAllFewerCoursesButton from '../show-all-fewer-courses-button';
import './styles.scss';

const cardsPerSlide = 3;
const getSlidesCollection = courses => chunk(courses, cardsPerSlide);

const CoursesCarousel = ({
  topTitle,
  mainTitle,
  className,
  courses,
  onPlayCoursePreview,
  isLoading,
  isAvailableCourses,
}) => {
  const carouselRef = useRef(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(true);
  const [showAllCourses, setShowAllCourses] = useState(isAvailableCourses);
  const slidesCollection = useMemo(() => getSlidesCollection(courses), [courses]);

  const disabledPrevControl = showAllCourses || activeSlideIndex === 0;
  const disabledNextControl = showAllCourses || activeSlideIndex === slidesCollection.length - 1;

  const slideNext = () => {
    carouselRef.current.next();
  };

  const slidePrev = () => {
    carouselRef.current.prev();
  };

  const renderCarouselSlide = slideCourses => {
    if (courses.length === 2) {
      return (
        <div className="courses-carousel__two-courses-slide-wrapper">
          {slideCourses.map(course => (
            <div key={course.id} className="courses-carousel__two-courses-slide-item">
              <CourseCard course={course} onPlayCoursePreview={onPlayCoursePreview} />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="courses-carousel__three-courses-slide-wrapper">
        {slideCourses.map(course => (
          <div key={course.id} className="courses-carousel__three-courses-slide-item">
            <CourseCard course={course} onPlayCoursePreview={onPlayCoursePreview} />
          </div>
        ))}
      </div>
    );
  };

  const renderCarouselSlides = () => {
    if (!showCarousel) return null;
    return slidesCollection.map((slideCourses, index) => (
      <Carousel.Item key={index}>{renderCarouselSlide(slideCourses)}</Carousel.Item>
    ));
  };

  if (isLoading) {
    return <Loader />;
  }

  if (courses.length === 1) {
    return (
      <SingleCourseCard
        isAvailableCourse={isAvailableCourses}
        course={courses[0]}
        onPlayCoursePreview={onPlayCoursePreview}
      />
    );
  }

  return (
    <div className={classNames('courses-carousel', className)}>
      <div className="courses-carousel__header">
        <CoursesTitle
          topTitle={topTitle}
          mainTitle={mainTitle}
          isAvailableCourses={isAvailableCourses}
          centered={courses.length === 2}
        />
        {!isAvailableCourses && courses.length > cardsPerSlide && (
          <div className="courses-carousel__controls-wrapper">
            <Button
              type="green"
              className={classNames(
                'courses-carousel__control',
                disabledPrevControl && 'courses-carousel__control_disabled',
              )}
              text={<img src={arrowLeftIcon} alt="switch-previous" />}
              onClick={slidePrev}
              disabled={disabledPrevControl}
            />
            <Button
              type="green"
              className={classNames(
                'courses-carousel__control',
                disabledNextControl && 'courses-carousel__control_disabled',
              )}
              text={<img src={arrowRightIcon} alt="switch-next" />}
              onClick={slideNext}
              disabled={disabledNextControl}
            />
          </div>
        )}
      </div>
      <Carousel
        ref={carouselRef}
        interval={null}
        controls={false}
        indicators={false}
        touch={false}
        wrap={false}
        onSlide={slideIndex => {
          setActiveSlideIndex(slideIndex);
        }}
      >
        {renderCarouselSlides()}
        <CoursesCollapsibleSheet
          courses={courses}
          onPlayCoursePreview={onPlayCoursePreview}
          onEntered={() => setShowCarousel(false)}
          onExit={() => setShowCarousel(true)}
          sheetIsExpanded={showAllCourses}
        />
      </Carousel>
      {!isAvailableCourses && courses.length > cardsPerSlide && (
        <div className="courses-carousel__show-all-fewer-button-wrapper">
          <ShowAllFewerCoursesButton
            onClick={() => setShowAllCourses(!showAllCourses)}
            showAllCourses={showAllCourses}
          />
        </div>
      )}
    </div>
  );
};

export default CoursesCarousel;
